// ============================================================
// Общие пресеты карт/модов: динамический пресет «Все карты FFA Лига» —
// объединение карт общих пресетов, имя которых начинается с «FFA Лига».
// Используется рандомайзером (/tools/random) и менеджером матча (/tools/match).
// Из этих же списков автоматически вычисляются типы микро / макро / гибрид
// и «FFA Лига» — вручную они не ставятся и в БД не хранятся.
// ============================================================

import { MAP_TYPES, TEAM_FORMATS, type MapType, type TeamFormat } from '../api/game-refs';
import { t } from '../i18n';

/** Минимальные поля пресета, нужные для объединения карт. */
export interface MapPresetLike {
    name: string;
    shared: boolean;
    map_ids: number[];
}

/** Тип динамического пресета «объединение пресетов лиги» (значение `d:league`). */
export const LEAGUE_DYNAMIC_TYPE = 'league';

/** Вычисляемый тип карты «FFA Лига»: карта входит в общие пресеты лиги. */
export const LEAGUE_MAP_TYPE = 'ffa_league';

/** Вычисляемые типы карт (зависят от общих списков, не от БД). */
export type AutoMapType = 'micro' | 'macro' | 'hybrid' | typeof LEAGUE_MAP_TYPE;

/** Стиль игры, вычисляемый по имени списка (микро / макро / гибрид). */
export type MapStyleType = 'micro' | 'macro' | 'hybrid';
export const MAP_STYLE_TYPES: readonly MapStyleType[] = ['micro', 'macro', 'hybrid'];

/** Тип карты для фильтров: ручной (из БД) либо вычисляемый из списков. */
export type MapFilterType = MapType | AutoMapType;

/** Вычисляемый стиль игры — для отдельной подсветки бейджа. */
export function isMapStyleType(type: MapFilterType): type is MapStyleType {
    return (MAP_STYLE_TYPES as readonly string[]).includes(type);
}

/** Порядок типов в фильтрах: ручные, стиль игры, «FFA Лига». */
export const MAP_FILTER_TYPES: readonly MapFilterType[] = [
    ...MAP_TYPES,
    'micro',
    'macro',
    'hybrid',
    LEAGUE_MAP_TYPE,
];

/** Префиксы имён общих пресетов, попадающих в объединение. */
const LEAGUE_PREFIXES = ['ffa лига', 'ffa league'];

/** Ключевые слова в имени общего списка → вычисляемый тип стиля игры. */
const STYLE_KEYWORDS: { type: 'micro' | 'macro' | 'hybrid'; keywords: string[] }[] = [
    { type: 'micro', keywords: ['микро', 'micro'] },
    { type: 'macro', keywords: ['макро', 'macro'] },
    { type: 'hybrid', keywords: ['гибрид', 'hybrid'] },
];

/** Наборы id карт по вычисляемым типам (лига целиком + стиль игры). */
export interface LeagueMapTypes {
    league: Set<number>;
    micro: Set<number>;
    macro: Set<number>;
    hybrid: Set<number>;
}

/** Общий пресет относится к лиге (по префиксу имени). */
export function isLeaguePreset(preset: { name: string; shared: boolean }): boolean {
    if (!preset.shared) return false;
    const name = preset.name.trim().toLowerCase();
    return LEAGUE_PREFIXES.some((prefix) => name.startsWith(prefix));
}

/**
 * Вычисляет автоматические типы карт по общим спискам лиги:
 * «FFA Лига» — любое попадание, микро/макро/гибрид — по слову в имени списка.
 */
export function leagueMapTypes(presets: MapPresetLike[]): LeagueMapTypes {
    const result: LeagueMapTypes = {
        league: new Set<number>(),
        micro: new Set<number>(),
        macro: new Set<number>(),
        hybrid: new Set<number>(),
    };
    for (const preset of presets) {
        if (!isLeaguePreset(preset)) continue;
        const name = preset.name.trim().toLowerCase();
        for (const id of preset.map_ids) result.league.add(id);
        for (const { type, keywords } of STYLE_KEYWORDS) {
            if (!keywords.some((keyword) => name.includes(keyword))) continue;
            for (const id of preset.map_ids) result[type].add(id);
        }
    }
    return result;
}

/** id карт из всех общих пресетов лиги (объединение). */
export function leagueMapIds(presets: MapPresetLike[]): Set<number> {
    return leagueMapTypes(presets).league;
}

/** Подпись формата командной карты («2на2», «2на2на2на2» и т.п.). */
export function teamFormatLabel(format: TeamFormat): string {
    return t(`map_format.${format}` as any);
}

/**
 * Форматы карты: имеют смысл только у командной карты (тип team),
 * у остальных список не показываем и в фильтрах не учитываем.
 */
export function mapTeamFormats(map: {
    map_types?: MapType[] | null;
    mapTypes?: MapType[] | null;
    team_formats?: TeamFormat[] | null;
    teamFormats?: TeamFormat[] | null;
}): TeamFormat[] {
    const types = map.map_types ?? map.mapTypes ?? [];
    if (!types.includes('team')) return [];
    return (map.team_formats ?? map.teamFormats ?? []).filter((f) => TEAM_FORMATS.includes(f));
}

/** Типы карты для показа/фильтра: ручные из БД + вычисляемые из списков лиги. */
export function mapTypesWithLeague(
    map: { id: number; map_types?: MapType[] | null; mapTypes?: MapType[] | null },
    league: LeagueMapTypes
): MapFilterType[] {
    const types: MapFilterType[] = [...(map.map_types ?? map.mapTypes ?? [])];
    if (league.micro.has(map.id)) types.push('micro');
    if (league.macro.has(map.id)) types.push('macro');
    if (league.hybrid.has(map.id)) types.push('hybrid');
    if (league.league.has(map.id)) types.push(LEAGUE_MAP_TYPE);
    return types;
}
